import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Card, TextField, InputAdornment, Stack, Typography, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch,
  useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PageHeader } from "../components/PageHeader.js";
import { DialogHelp } from "../components/DialogHelp.js";
import { EmptyState } from "../components/EmptyState.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { DataGridRowActionsMenu } from "../components/DataGridRowActionsMenu.js";
import { ComboSuppliers, ComboAcqServices } from "../components/combos/index.js";
import { useTranslation } from "react-i18next";
import { useApi } from "../hooks/useApi.js";
import { formLabel } from "../lib/labels.js";
import type { Cost, Supplier, AcquiredService } from "@kappa-v2/contracts";

// Nome leggibile del fornitore (azienda e/o persona), con fallback all'id.
const supplierLabel = (s?: Supplier): string => {
  if (!s) return "";
  const person = `${s.lastName ?? ""} ${s.firstName ?? ""}`.trim();
  if (s.companyName && person) return `${s.companyName} — ${person}`;
  return s.companyName || person;
};

const fmtDate = (d?: Date | string | null) =>
  d ? new Date(d).toLocaleDateString("it-IT") : "—";

const todayISO = () => new Date().toISOString().slice(0, 10);
const toDateInput = (d?: Date | string | null) =>
  d ? new Date(d).toISOString().slice(0, 10) : todayISO();

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR + 2 - 2024 + 1 }, (_, i) => `${CURRENT_YEAR + 2 - i}`);

// Chiavi i18n dei metodi di pagamento (come in produzione: form.payment.*)
const PAYMENT_TYPES = [
  "form.payment.cash",
  "form.payment.bankTransfer",
  "form.payment.creditCard",
  "form.payment.debitCard",
  "form.payment.electronicPayment"
];

interface FormValues {
  _id?: string;
  serviceAcquiredId: string;
  supplierId: string;
  priceServiceAcquired: number;
  datePaid: string;
  paymentType: string;
  settled: boolean;
  // denormalizzati dal servizio acquisito
  nameServiceAcquired: string;
  descriptionServiceAcquired: string;
  emailNotificationServiceAcquired: boolean;
  serviceType: string;
  genericType: string;
}

const EMPTY: FormValues = {
  serviceAcquiredId: "", supplierId: "", priceServiceAcquired: 0,
  datePaid: todayISO(), paymentType: "form.payment.cash", settled: false,
  nameServiceAcquired: "", descriptionServiceAcquired: "",
  emailNotificationServiceAcquired: false, serviceType: "", genericType: ""
};

export function CostsPage() {
  const { t } = useTranslation();
  const { call, openPdf } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState<Cost[]>([]);
  const [suppliers, setSuppliers] = useState<Record<string, string>>({});
  const [acqServices, setAcqServices] = useState<AcquiredService[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(`${CURRENT_YEAR}`);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Cost | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Cost | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    call<{ data: Cost[] } | Cost[]>(`/api/v1/costs?year=${encodeURIComponent(year)}`)
      .then(r => setRows(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [call, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Anagrafica fornitori ridotta per risolvere supplierId → nome.
  useEffect(() => {
    call<{ data?: Supplier[] } | Supplier[]>("/api/v1/suppliers/reduced?active=all", { silent: true, noLoader: true })
      .then((r) => {
        const list = Array.isArray(r) ? r : (r.data ?? []);
        setSuppliers(Object.fromEntries(list.map((s) => [String(s._id), supplierLabel(s)])));
      })
      .catch(() => setSuppliers({}));
  }, [call]);

  // Catalogo servizi acquisiti per precompilare il costo dalla selezione.
  useEffect(() => {
    call<{ data?: AcquiredService[] } | AcquiredService[]>("/api/v1/acqservices", { silent: true, noLoader: true })
      .then((r) => setAcqServices(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setAcqServices([]));
  }, [call]);

  const acqById = useMemo(() => {
    const m = new Map<string, AcquiredService>();
    acqServices.forEach((a) => a._id && m.set(String(a._id), a));
    return m;
  }, [acqServices]);

  const schema = Yup.object({
    supplierId: Yup.string().required(t("costs.form.supplierRequired")),
    serviceAcquiredId: Yup.string().required(t("costs.form.serviceRequired")),
    priceServiceAcquired: Yup.number().min(0.01).required(t("costs.form.priceRequired")),
    paymentType: Yup.string(),
    datePaid: Yup.string().required()
  });

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const body = {
          serviceAcquiredId: values.serviceAcquiredId,
          supplierId: values.supplierId,
          priceServiceAcquired: Number(values.priceServiceAcquired),
          datePaid: values.datePaid,
          paymentType: values.paymentType,
          settled: values.settled,
          nameServiceAcquired: values.nameServiceAcquired,
          descriptionServiceAcquired: values.descriptionServiceAcquired,
          emailNotificationServiceAcquired: values.emailNotificationServiceAcquired,
          serviceType: values.serviceType,
          genericType: values.genericType
        };
        if (editTarget?._id) {
          await call(`/api/v1/costs/${editTarget._id}`, { method: "PUT", body: { ...body, _id: editTarget._id } });
        } else {
          await call("/api/v1/costs", { method: "POST", body });
        }
        setDialogOpen(false);
        fetchAll();
      } finally { setSaving(false); }
    }
  });

  // Selezione servizio acquisito → precompila i campi denormalizzati del costo.
  const onSelectAcqService = (id: string) => {
    const a = acqById.get(id);
    formik.setFieldValue("serviceAcquiredId", id);
    if (!a) return;
    formik.setFieldValue("nameServiceAcquired", a.name ?? "");
    formik.setFieldValue("descriptionServiceAcquired", a.description ?? "");
    formik.setFieldValue("priceServiceAcquired", a.price ?? 0);
    formik.setFieldValue("emailNotificationServiceAcquired", Boolean(a.emailNotification));
    formik.setFieldValue("serviceType", a.serviceType ?? "");
    formik.setFieldValue("genericType", a.genericType ?? "");
  };

  const openAdd = () => { setEditTarget(null); formik.resetForm({ values: { ...EMPTY, datePaid: todayISO() } }); setDialogOpen(true); };
  const openEdit = (c: Cost) => {
    setEditTarget(c);
    formik.resetForm({ values: {
      _id: c._id,
      serviceAcquiredId: c.serviceAcquiredId,
      supplierId: c.supplierId,
      priceServiceAcquired: c.priceServiceAcquired ?? 0,
      datePaid: toDateInput(c.datePaid),
      paymentType: c.paymentType ?? "form.payment.cash",
      settled: Boolean(c.settled),
      nameServiceAcquired: c.nameServiceAcquired ?? "",
      descriptionServiceAcquired: c.descriptionServiceAcquired ?? "",
      emailNotificationServiceAcquired: Boolean(c.emailNotificationServiceAcquired),
      serviceType: c.serviceType ?? "",
      genericType: c.genericType ?? ""
    }});
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/costs/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null);
    fetchAll();
  };

  const filtered = rows.filter(r =>
    `${suppliers[r.supplierId ?? ""] ?? ""} ${r.nameServiceAcquired ?? ""} ${r.descriptionServiceAcquired ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );
  const total = filtered.reduce((s, r) => s + (r.priceServiceAcquired ?? 0), 0);

  const buildActions = (c: Cost) => [
    { key: "edit", label: t("costs.editAction"), description: t("costs.editActionDesc"), icon: <EditIcon fontSize="small" />, onClick: () => openEdit(c) },
    { key: "del", label: t("costs.deleteAction"), description: t("costs.deleteActionDesc"), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteTarget(c) }
  ];

  const columns: GridColDef[] = [
    {
      field: "supplier", headerName: t("costs.supplier"), flex: 1, minWidth: 180,
      valueGetter: (_v, row: Cost) => suppliers[row.supplierId ?? ""] ?? row.supplierId ?? "",
      renderCell: (params: GridRenderCellParams<Cost>) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {suppliers[params.row.supplierId ?? ""] || params.row.supplierId || "—"}
        </Typography>
      )
    },
    {
      field: "nameServiceAcquired", headerName: t("costs.acquiredService"), flex: 1, minWidth: 200,
      renderCell: (params: GridRenderCellParams<Cost>) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{params.row.nameServiceAcquired}</Typography>
          {params.row.descriptionServiceAcquired &&
            <Typography variant="caption" color="text.secondary" noWrap>{params.row.descriptionServiceAcquired}</Typography>}
        </Box>
      )
    },
    {
      field: "paymentType", headerName: t("costs.method"), width: 150,
      renderCell: (params: GridRenderCellParams<Cost>) => (
        <Chip size="small" label={params.row.paymentType ? formLabel(params.row.paymentType) : "—"} variant="outlined" sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "datePaid", headerName: t("common.date"), width: 120,
      renderCell: (params: GridRenderCellParams<Cost>) => (
        <Typography variant="body2">{fmtDate(params.row.datePaid)}</Typography>
      )
    },
    {
      field: "settled", headerName: t("costs.settled"), width: 100, align: "center", headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Cost>) => (
        <Chip size="small" label={params.row.settled ? t("common.yes") : t("common.no")}
          color={params.row.settled ? "success" : "default"} variant="outlined" sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "priceServiceAcquired", headerName: t("common.amount"), width: 130, align: "right", headerAlign: "right",
      renderCell: (params: GridRenderCellParams<Cost>) => (
        <Typography variant="body2" fontWeight={700} color="error.dark">
          € {(params.row.priceServiceAcquired ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 56, sortable: false, align: "center", headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Cost>) => (
        <DataGridRowActionsMenu ariaLabel={t("common.actions")} items={buildActions(params.row)} />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={t("costs.title")}
        subtitle={t("costs.subtitle")}
        onRefresh={fetchAll}
        actionLabel={t("costs.register")}
        onAction={openAdd}
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>{t("common.year")}</InputLabel>
              <Select label={t("common.year")} value={year} onChange={(e) => setYear(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}>
                {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
              onClick={() => openPdf(`/api/v1/costs/summary/${year}/pdf`)} disabled={filtered.length === 0}>
              {t("costs.exportPdf")}
            </Button>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" placeholder={t("costs.searchPlaceholder")}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 240 } }}
            />
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">{t("costs.totalExpenses")}</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="error.dark">
                € {total.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </Typography>
            </Stack>
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("costs.none")} description={t("costs.noneDesc")} actionLabel={t("costs.register")} onAction={openAdd} />
            : isMobile
              ? (
                <Box>
                  {filtered.map((c, idx) => (
                    <Box key={c._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {suppliers[c.supplierId ?? ""] || c.supplierId || "—"}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>{c.nameServiceAcquired}</Typography>
                          {c.descriptionServiceAcquired &&
                            <Typography variant="caption" color="text.secondary" display="block">{c.descriptionServiceAcquired}</Typography>}
                        </Box>
                        <DataGridRowActionsMenu ariaLabel={t("common.actions")} items={buildActions(c)} />
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight={700} color="error.dark">
                          € {(c.priceServiceAcquired ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                        </Typography>
                        <Chip size="small" variant="outlined" label={c.paymentType ? formLabel(c.paymentType) : "—"} sx={{ fontSize: "0.72rem" }} />
                        <Typography variant="caption" color="text.secondary">{fmtDate(c.datePaid)}</Typography>
                        <Chip size="small" variant="outlined"
                          label={`${t("costs.settled")}: ${c.settled ? t("common.yes") : t("common.no")}`}
                          color={c.settled ? "success" : "default"} sx={{ fontSize: "0.72rem" }} />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )
              : <DataGrid rows={filtered} columns={columns} getRowId={r => r._id ?? Math.random().toString()}
                  loading={loading} autoHeight rowHeight={60} pageSizeOptions={[25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  disableRowSelectionOnClick sx={{ border: "none" }}
                />
          }
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            {editTarget ? t("costs.editCost") : t("costs.register")}
            <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.costs") }]} />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ComboSuppliers label={t("costs.form.supplierLabel")}
                  value={formik.values.supplierId} onChange={(id) => formik.setFieldValue("supplierId", id)}
                  error={formik.touched.supplierId && Boolean(formik.errors.supplierId)}
                  helperText={formik.touched.supplierId ? formik.errors.supplierId : undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <ComboAcqServices label={t("costs.form.serviceLabel")}
                  value={formik.values.serviceAcquiredId} onChange={onSelectAcqService}
                  error={formik.touched.serviceAcquiredId && Boolean(formik.errors.serviceAcquiredId)}
                  helperText={formik.touched.serviceAcquiredId ? formik.errors.serviceAcquiredId : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t("costs.form.priceLabel")} type="number"
                  {...formik.getFieldProps("priceServiceAcquired")} inputProps={{ step: 0.01, min: 0 }}
                  error={formik.touched.priceServiceAcquired && Boolean(formik.errors.priceServiceAcquired)}
                  helperText={formik.touched.priceServiceAcquired && formik.errors.priceServiceAcquired}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t("costs.form.dateLabel")} type="date"
                  InputLabelProps={{ shrink: true }}
                  {...formik.getFieldProps("datePaid")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("costs.form.methodLabel")}</InputLabel>
                  <Select label={t("costs.form.methodLabel")} {...formik.getFieldProps("paymentType")}>
                    {PAYMENT_TYPES.map(m => <MenuItem key={m} value={m}>{formLabel(m)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={<Switch checked={formik.values.settled}
                    onChange={(e) => formik.setFieldValue("settled", e.target.checked)} />}
                  label={t("costs.form.settledLabel")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? t("common.saving") : editTarget ? t("common.save") : t("costs.form.submit")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("costs.deleteConfirm", { name: deleteTarget?.nameServiceAcquired ?? "" })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
